import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { CostsAndCharges, ProductGovernance, RegulatoryDocument } from 'models/streaming';
import { TradeDirection } from 'models/TradeDirection';
import { TradeWay } from 'models/TradeWay';
import type { QuoteDetailsData } from 'state/quotes/details/details.model';
import type { Collection } from 'state/utils/types';

import type { QuoteType } from '../infos/infos.model';
import type { QuoteInfos } from '../pendings/pendings.model';
import type { PriceEnhancement } from '../trades/trades.model';

export interface QuoteActivities {
  isBasketOptimizerEnabled: boolean;
}

export type QuoteWithRegulation = {
  quote: QuoteResult;
  costAndCharge?: CostsAndCharges;
  kidDocumentation?: RegulatoryDocument;
  productGovernanceDocumentation?: RegulatoryDocument;
  productGovernanceAssessment?: ProductGovernance;
};
export type QuoteResultStatus = 'Quoted' | 'Rejected' | 'Failed';

export type CommitMode = 'Executable' | 'Indicative';

export type SolveResultFormat = 'Percent' | 'Number';

export function mapFromCommitModeToQuoteType(commitMode: CommitMode | undefined): QuoteType | undefined {
  switch (commitMode) {
    case 'Executable':
      return 'Tradable';
    case 'Indicative':
      return 'Indicative';
    default:
      return undefined;
  }
}

export type WarningMessage = Readonly<{
  code: string;
  message: string;
}>;
export type QuoteResult = Readonly<
  {
    sidePanelDetails?: readonly ReadonlyValueDefinition[];
    quoteId: string;
    negotiationId?: string;
    quoteExternalId: string;
    productSubType?: string;
    quoteType?: QuoteType;
    createdAt?: string;
    estimatedQuotingDurationInMillis?: number;
    priceEnhancement?: PriceEnhancement;
    pricingTime?: string;
    quoteDetails?: QuoteDetailsData;
    rejections?: NextWorkflowError[];
    solveResult?: number;
    pricerRfqId?: string;
    solveResultFormat?: SolveResultFormat;
    status?: QuoteResultStatus;
    warnings?: WarningMessage[];
    isExecOnlyAddon?: boolean;
    availableActivities?: QuoteActivities;
    commitMode?: CommitMode;
    tradeExecutionWindowInMilliseconds?: number;
    tradeExecutionWindowStartTime?: number;
    tradeWay?: TradeWay;
    tradeDirection?: TradeDirection;
  } & QuoteInfos
>;

export type QuoteResultsMap = Collection<QuoteResult>;
